import { GRADEBOOK_SETTINGS } from '@macmillan-learning/gradebook-shared';
import dayjs from '../../../../../utils/dayjs';
import { useDispatch, useSelector } from 'react-redux';

import { toggleCategoryExpandCollapse } from '../../../../../actions/gradebook';
import { ISG_REPORT_VIEW_OPTIONS } from '../../../../../utils/constants';
import CategoryNameButton from '../../../../CategoryNameButton/CategoryNameButton';
import { getGradingType } from '../../utils';
import s from './Accordion.module.css';
import { PrimaryMetrics } from './PrimaryMetrics';
import { AdditionalMetrics } from './AdditionalMetrics';
import classNames from 'classnames';

export const Accordion = ({
  children,
  currentView,
  sectionData,
  previousSectionData,
  user,
  settings,
  isMobile,
}) => {
  // Complex mode: use Redux state
  const dispatch = useDispatch();
  const collapsedCategoryIds = useSelector(
    (state) => state.gradebook.collapsedCategoryIds,
  );

  // Complex mode (existing business logic)
  const { assignments = [], id, total, name, isUngraded } = sectionData;
  const lowest =
    sectionData?.dropLowestNumGrades ||
    sectionData?.drop_lowest_num_grades ||
    total?.drop_lowest_num_grades;
  const weightVal = sectionData?.weight || sectionData?.total?.weight;

  const categoryId = id ?? assignments?.[0]?.gradebook_category_id;
  const forceCloseEmptyAccordion = assignments?.length === 0;
  const isOpen =
    !forceCloseEmptyAccordion && !collapsedCategoryIds.includes(categoryId);
  const gradingType = getGradingType(settings);

  // Toggle handler
  const handleToggle = () => {
    dispatch(toggleCategoryExpandCollapse(categoryId));
  };

  const shouldRenderWeekRange = () => {
    if (currentView !== ISG_REPORT_VIEW_OPTIONS.WEEK) {
      return false;
    }

    // Ensure a valid date range
    const { from, to } = sectionData.dateRange;
    return !(!from || !to || from > to || dayjs(from).isSame(to, 'date'));
  };

  const renderWeekRange = () => {
    const { from, to } = sectionData.dateRange;

    const dateFromStr = from ? dayjs(from).format('MMMM D') : null;

    let dateToStr;
    if (from && to && from?.getMonth() === to?.getMonth()) {
      dateToStr = dayjs(to).format('D');
    } else {
      dateToStr = dayjs(to).format('MMMM D');
    }

    const weekRangeLabel = `${dateFromStr} - ${dateToStr}`;
    return <div className={s.weekRangeLabel}>{weekRangeLabel}</div>;
  };

  const renderPrimaryMetrics = () => (
    <PrimaryMetrics
      currentView={currentView}
      isAccordionOpen={isOpen}
      total={total}
      assignments={assignments}
      isMobile={isMobile}
      isUngraded={isUngraded}
      settings={settings}
      previousSectionData={previousSectionData}
      user={user}
    />
  );

  const renderDetailsHeader = () => {
    return (
      <div className={s.detailsHeader}>
        <div className={s.assignmentDetailsHeader}>Assignment Details</div>
        <div className={s.scoreDetailsHeader}>Score</div>
      </div>
    );
  };

  return (
    <div
      className={classNames(s.accordion, {
        [s.openPadding]: isOpen && !isMobile,
        [s.closedPadding]: !isOpen,
      })}
    >
      <div
        className={s.accordionButton}
        data-test-id="individual-student-gb-collapse-gradebook-category"
      >
        <div className={s.accordionLabel}>
          <CategoryNameButton
            showWeightLabel={
              currentView === ISG_REPORT_VIEW_OPTIONS.CATEGORY &&
              gradingType === GRADEBOOK_SETTINGS.gradingTypeWeighted
            }
            category={{
              name,
              drop_lowest_num_grades: lowest,
              weight: weightVal,
            }}
            categoryNameClassName={s.sectionName}
            weightsClassName={s.sectionWeights}
            collapseButtonClassName={s.collapseButton}
            active={isOpen}
            activeIcon="chevron_down"
            inactiveIcon="chevron_right"
            handleCategoryTabToggle={handleToggle}
            testId="individual-student-gb-category-label"
          />
          {shouldRenderWeekRange() && renderWeekRange()}
        </div>
        {!isOpen && !isMobile && renderPrimaryMetrics()}
      </div>
      {isOpen && renderPrimaryMetrics()}
      {isOpen && isMobile && renderDetailsHeader()}
      {isOpen && assignments?.length > 0 && children}
      {isOpen && (
        <AdditionalMetrics
          currentView={currentView}
          sectionData={sectionData}
          isMobile={isMobile}
        />
      )}
    </div>
  );
};
